import React, { useEffect, useState } from "react";
import { Table, Button, Modal, message, Card, Input, Alert, Image } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  BarsOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ManagementForm from "./managementForm";
import CommonPagination from "../../components/common/pagination";

const Management = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [management, setManagement] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);
  const [previewVisible, setPreviewVisible] = useState();
  const [previewImage, setPreviewImage] = useState();
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const fieldLabels = {
    name: "Name",
    departement: "Departement",
    image: "Image",
    role: "Role",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const fetchManagement = async (
    pageNumber = 1,
    searchText = "",
    sortName = "desc",
    sortRole = " esc",
  ) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/management`, {
        params: {
          page: pageNumber,
          limit,
          search: searchText,
          sortBy,
          sortOrder,
        },
      });
      console.log("FetchTeam response:", res);
      setManagement(res.data.data.rows || []);
      setTotal(res.data.data.count || 0);
    } catch (error) {
      console.error("FetchTeam error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManagement(page, searchText);
  }, [page, searchText, sortBy, sortOrder]);

  const handleSubmit = async (formData) => {
    try {
      if (editData) {
        await axios.put(`${BASE_URL}/api/management/${editData.id}`, formData);
        setSuccessMsg("Management updated");
        setTimeout(() => setSuccessMsg(null), 3000);
      } else {
        await axios.post(`${BASE_URL}/api/management`, formData);
        setSuccessMsg("Management Added");
        setTimeout(() => setSuccessMsg(null), 3000);
      }
      setOpen(false);
      setEditData(null);
      fetchManagement(1);
    } catch (err) {
      message.error("Failed to submit management");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/management/${id}`);
      setSuccessMsg("management deleted");
      setTimeout(() => setSuccessMsg(null), 3000);

      fetchManagement(page);
    } catch (err) {
      message.error("Failed to delete management");
    }
  };

  const columns = [
    {
      title: "No",
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          const newSort = sortOrder === "asc" ? "desc" : "asc";
          setSortBy("name");
          setSortOrder(newSort);
          fetchManagement(page, searchText, "name", newSort);
        },
      }),
    },
    { title: "Department", dataIndex: "department" },
    {
      title: "Role",
      dataIndex: "role",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          const newSort = sortOrder === "asc" ? "desc" : "asc";
          setSortBy("role");
          setSortOrder(newSort);
          fetchManagement(page, searchText, "role", newSort);
        },
      }),
    },
    {
      title: "Image",
      dataIndex: "image",
      render: (image) => {
        return image ? (
          <img
            src={`${BASE_URL}${image}`}
            alt="management"
            width={60}
            style={{ objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          "No Image"
        );
      },
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Button
            style={{
              // backgroundColor: '#fff',
              borderColor: "#ec4899",
              color: "#ec4899",
              marginRight: 8,
            }}
            icon={<BarsOutlined />}
            type="link"
            onClick={() => {
              setDetailData({
                ...record,
                image: [
                  {
                    uid: "-1",
                    name: "image.jpg",
                    status: "done",
                    url: `${BASE_URL}${record.image}`,
                  },
                ],
              });
              setOpenDetail(true);
            }}
          />

          <Button
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
              marginRight: 8,
            }}
            icon={<EditOutlined />}
            type="link"
            onClick={() => {
              setEditData({
                ...record,
                image: [
                  {
                    uid: "-1",
                    name: "image.jpg",
                    status: "done",
                    url: `${BASE_URL}${record.image}`,
                  },
                ],
              });
              setOpen(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#374151",
              borderColor: "#374151",
              color: "#fff",
              marginRight: 8,
            }}
            icon={<DeleteOutlined />}
            type="link"
            onClick={() => {
              Modal.confirm({
                title: "Are you sure you want to delete this team?",
                centered: true,
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDelete(record.id),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Card title="Management">
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          style={{
            marginBottom: 16,
            borderRadius: 8,
            backgroundColor: "#f6ffed",
            color: "#389e0d",
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Input.Search
          placeholder="Search Management.."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
        <Button
          style={{
            backgroundColor: "#ec4899",
            borderColor: "#ec4899",
            color: "#fff",
          }}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          + Add Management
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={management}
        rowKey="id"
        loading={loading}
        style={{ marginTop: 20 }}
        pagination={false}
      />

      <CommonPagination
        current={page}
        pageSize={limit}
        total={total}
        onChange={(newPage) => setPage(newPage)}
      />

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditData(null);
        }}
        footer={null}
        destroyOnClose
      >
        <ManagementForm
          initialValues={editData}
          onSuccess={handleSubmit}
          editMode={!!editData}
        />
      </Modal>

      <Modal
        title="Management Detail"
        open={openDetail}
        onCancel={() => setOpenDetail(false)}
        footer={[]}
      >
        {detailData && (
          <Table
            dataSource={Object.entries(detailData)
              .filter(([key]) => key !== "id")
              .map(([key, value]) => ({
                key,
                field: fieldLabels[key] || key,
                value:
                  key === "image" && value
                    ? Array.isArray(value)
                      ? value.map((img, idx) => {
                          const src = img.url ? img.url : `${BASE_URL}${img}`;
                          return (
                            <Image
                              key={idx}
                              src={src}
                              width={100}
                              style={{ marginRight: 8, borderRadius: 4 }}
                              preview={{
                                mask: (
                                  <EyeOutlined
                                    style={{ color: "white", fontSize: 18 }}
                                  />
                                ),
                              }}
                            />
                          );
                        })
                      : (() => {
                          const src = `${BASE_URL}${value}`;
                          return (
                            <Image
                              src={src}
                              width={100}
                              preview={{
                                mask: (
                                  <EyeOutlined
                                    style={{ color: "white", fontSize: 18 }}
                                  />
                                ),
                              }}
                            />
                          );
                        })()
                    : value?.toString(),
              }))}
            columns={[
              {
                title: "Field",
                dataIndex: "field",
                key: "field",
                width: "30%",
                render: (text) => <strong>{text}</strong>,
              },
              {
                title: "Value",
                dataIndex: "value",
                key: "value",
              },
            ]}
            pagination={false}
            bordered
          />
        )}
      </Modal>

      <Modal
        visible={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <Image src={previewImage} width="100%" />
      </Modal>
    </Card>
  );
};

export default Management;
